import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  SHIPPING_METHOD,
  type Address,
  type ShippingMethod,
  useGetShippingRates,
} from '@frontend/api';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Center,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  OutlineCircleInfoIcon,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Tooltip,
  Text,
  VStack,
} from '@frontend/legacy-components';
import { toMoment } from '@frontend/utils';

import {
  PAYMENT_METHOD,
  SHIPPING_CARRIER,
  SHIPPING_GROUPING,
  SHIPPING_TYPE,
} from 'constants/all';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';

import { useAppSelector } from 'store/hooks';
import { getQuote } from 'store/quotes/selectors';
import { useFeatureFlagValue } from 'store/session/hooks';

import { formatPrice } from 'utils/formatters';

import {
  SHIPPING_CARRIER_LABEL,
  SHIPPING_METHOD_LABEL,
  SHIPPING_METHOD_RATES,
} from '../constants';
import { setShippingChoice, setShippingRate } from '../data/actions';
import {
  getPaymentMethod,
  getSelectedOptions,
  getShippingAccount,
  getShippingInformation,
  getShippingRate,
  getShippingType,
} from '../data/reducer';
import {
  ShippingAccount,
  ShippingCarrier,
  ShippingRate,
  ShippingType,
} from '../data/types';

const { EXPEDITED, ...shippingMethods } = SHIPPING_METHOD;

const LEGACY_SHIPPING_RATES: ShippingRate[] = SHIPPING_METHOD_RATES.map(
  (rate) => ({
    // For purchase-order shipping, we only need a method (token should not be sent)
    // but `ShippingRate` requires these fields.
    arrivesOn: '',
    token: '',
    method: rate.method,
    price: rate.price,
  }),
);

const SHIPPING_TYPE_LABEL = {
  [SHIPPING_TYPE.PICKUP]: 'No shipping fees',
  [SHIPPING_TYPE.CUSTOMER_ACCOUNT]: 'Use customer shipping account',
  [SHIPPING_TYPE.SUPPLIER_ACCOUNT]: 'Bill customer at time of shipment',
};

const SUPPLIER_ACCOUNT_CHOICES = {
  BILL_LATER: {
    value: 'supplier_account_bill_later',
    label: SHIPPING_TYPE_LABEL[SHIPPING_TYPE.SUPPLIER_ACCOUNT],
  },
  CHARGE_NOW: {
    value: 'supplier_account_charge_now',
    label: 'Charge customer now for shipping',
  },
} as const;

const getCarrierOption = (carrier: ShippingCarrier) => {
  return {
    value: carrier,
    label: SHIPPING_CARRIER_LABEL[carrier],
  };
};

const getMethodOption = (method: ShippingMethod) => {
  return {
    value: method,
    label: SHIPPING_METHOD_LABEL[method],
  };
};

const DEFAULT_SHIPPING_ACCOUNT_VALUES = {
  customersAccountNumber: undefined,
  customersCarrier: SHIPPING_CARRIER.UPS,
  shippingMethod: SHIPPING_METHOD.EARLY_OVERNIGHT,
  shipWhen: SHIPPING_GROUPING.BULK,
};

const useShippingOptionDisabledMessages = () => {
  const paymentMethod = useAppSelector(getPaymentMethod);
  const shippingInformation = useAppSelector(getShippingInformation);
  const hasShippingInformation = Boolean(shippingInformation);
  return {
    [SHIPPING_TYPE.SUPPLIER_ACCOUNT]:
      paymentMethod !== PAYMENT_METHOD.PURCHASE_ORDER
        ? `Payment Method must be Purchase Order ${
            !hasShippingInformation
              ? 'and a Ship To Address must be provided '
              : ''
          }to select this option`
        : !hasShippingInformation
        ? 'A Ship To Address must be provided above to select this option'
        : null,
    [SHIPPING_TYPE.CUSTOMER_ACCOUNT]:
      paymentMethod === PAYMENT_METHOD.NONE
        ? `Payment Method must be Purchase Order or Credit Card ${
            !hasShippingInformation
              ? 'and a Ship To Address must be provided '
              : ''
          }to select this option`
        : !hasShippingInformation
        ? 'A Ship To Address must be provided above to select this option'
        : null,
    [SHIPPING_TYPE.PICKUP]: hasShippingInformation
      ? null
      : 'A Ship To Address must be provided above to select this option',
  };
};

export const ShippingOptions = () => {
  const dispatch = useDispatch();
  const quote = useAppSelector(getQuote);
  const selectedOptions = useAppSelector(getSelectedOptions);
  const shippingType = useAppSelector(getShippingType);
  const shippingAccount = useAppSelector(getShippingAccount);
  const shippingRate = useAppSelector(getShippingRate);
  const paymentMethod = useAppSelector(getPaymentMethod);
  const shippingInformation = useAppSelector(getShippingInformation);
  const isCalculatedShippingEnabled = Boolean(
    useFeatureFlagValue(featureFlagKeys.isCalculatedShippingEnabled),
  );
  const optionDisabledMessages = useShippingOptionDisabledMessages();

  const shippingAccountFormValues = useMemo(
    () => shippingAccount ?? DEFAULT_SHIPPING_ACCOUNT_VALUES,
    [shippingAccount],
  );

  const hasShippingInformation = Boolean(shippingInformation?.address);
  const canFetchCalculatedShippingRates =
    isCalculatedShippingEnabled &&
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
    hasShippingInformation &&
    Boolean(quote?.digiQuoteKey) &&
    Object.keys(selectedOptions).length > 0;

  const { data: shippingRatesData, isFetching: isFetchingCalculatedShipping } =
    useGetShippingRates(
      {
        quoteKey: quote?.digiQuoteKey ?? '',
        // Hook params require an `Address`, but we're already guarding with `enabled`.
        // Provide a harmless fallback for the disabled case.
        address: shippingInformation?.address ?? ({} as Address),
        itemGroups: {
          default: Object.values(selectedOptions).map((option) => ({
            quantityId: String(option.quantity.id),
            expediteId: option.expedite ? String(option.expedite.id) : null,
            addOnCells: [],
            shipsOn: option.shipsOn ?? null,
          })),
        },
      },
      {
        queryOptions: { enabled: canFetchCalculatedShippingRates },
      },
    );

  const calculatedShippingRates = shippingRatesData?.shippingRates.default;
  const canShowChargeMeForShippingOption =
    paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
    hasShippingInformation &&
    isCalculatedShippingEnabled &&
    Boolean(calculatedShippingRates && calculatedShippingRates.length > 0);

  const updateShippingAccount = useCallback(
    (values: Partial<ShippingAccount>) => {
      const nextShippingAccount = {
        ...shippingAccountFormValues,
        ...values,
      };
      dispatch(
        setShippingChoice({
          shippingAccount: nextShippingAccount,
          shippingType: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
          shippingRate: undefined,
        }),
      );
    },
    [shippingAccountFormValues, dispatch],
  );

  const shippingRateFormValue = useMemo(
    () => shippingRate ?? LEGACY_SHIPPING_RATES[0],
    [shippingRate],
  );

  const updateShippingRate = useCallback(
    (shippingRate?: ShippingRate) => {
      dispatch(setShippingRate({ shippingRate }));
    },
    [dispatch],
  );

  useEffect(() => {
    // Mirror old drawer behavior: if credit card shipping rates are unavailable,
    // prevent "supplier account" (charge-me-for-shipping) from being selected.
    if (
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
      isCalculatedShippingEnabled &&
      hasShippingInformation &&
      shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT &&
      !isFetchingCalculatedShipping &&
      (!calculatedShippingRates || calculatedShippingRates.length === 0)
    ) {
      dispatch(
        setShippingChoice({
          shippingType: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
          shippingAccount: shippingAccountFormValues,
          shippingRate: undefined,
        }),
      );
    }
  }, [
    calculatedShippingRates,
    dispatch,
    hasShippingInformation,
    isCalculatedShippingEnabled,
    isFetchingCalculatedShipping,
    paymentMethod,
    shippingAccountFormValues,
    shippingType,
  ]);

  useEffect(() => {
    if (
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
      shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT &&
      calculatedShippingRates &&
      calculatedShippingRates.length > 0
    ) {
      const currentToken = shippingRate?.token;
      const stillValid =
        currentToken &&
        calculatedShippingRates.some((rate) => rate.token === currentToken);
      if (!stillValid) {
        const firstRate = calculatedShippingRates[0];
        if (firstRate) {
          updateShippingRate(firstRate);
        }
      }
    }
  }, [
    calculatedShippingRates,
    paymentMethod,
    shippingRate?.token,
    shippingType,
    updateShippingRate,
  ]);

  type ShippingFeesChoice =
    | 'none'
    | ShippingType
    | (typeof SUPPLIER_ACCOUNT_CHOICES)[keyof typeof SUPPLIER_ACCOUNT_CHOICES]['value'];

  const selectedShippingFeesValue: ShippingFeesChoice = useMemo(() => {
    if (!shippingType) return 'none';
    if (shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT) {
      return canShowChargeMeForShippingOption
        ? SUPPLIER_ACCOUNT_CHOICES.CHARGE_NOW.value
        : SUPPLIER_ACCOUNT_CHOICES.BILL_LATER.value;
    }
    return shippingType;
  }, [canShowChargeMeForShippingOption, shippingType]);

  const handleChangeShippingType = useCallback(
    (choice: ShippingFeesChoice) => {
      switch (choice) {
        case 'none':
          dispatch(
            setShippingChoice({
              shippingType: undefined,
              shippingAccount: undefined,
              shippingRate: undefined,
            }),
          );
          return;
        case SHIPPING_TYPE.CUSTOMER_ACCOUNT:
          dispatch(
            setShippingChoice({
              shippingType: SHIPPING_TYPE.CUSTOMER_ACCOUNT,
              shippingAccount: shippingAccountFormValues,
              shippingRate: undefined,
            }),
          );
          return;
        case SHIPPING_TYPE.PICKUP:
          dispatch(
            setShippingChoice({
              shippingType: SHIPPING_TYPE.PICKUP,
              shippingAccount: undefined,
              shippingRate: undefined,
            }),
          );
          return;
        case SUPPLIER_ACCOUNT_CHOICES.CHARGE_NOW.value: {
          const nextRate =
            calculatedShippingRates?.find(
              (rate) => rate.token === shippingRate?.token,
            ) ?? calculatedShippingRates?.[0];
          dispatch(
            setShippingChoice({
              shippingType: SHIPPING_TYPE.SUPPLIER_ACCOUNT,
              shippingAccount: undefined,
              shippingRate: nextRate ?? shippingRateFormValue,
            }),
          );
          return;
        }
        case SUPPLIER_ACCOUNT_CHOICES.BILL_LATER.value:
          dispatch(
            setShippingChoice({
              shippingType: SHIPPING_TYPE.SUPPLIER_ACCOUNT,
              shippingAccount: undefined,
              shippingRate: shippingRateFormValue,
            }),
          );
          return;
        default:
          // Fallback for any future `ShippingType` values
          dispatch(
            setShippingChoice({
              shippingType: choice as ShippingType,
              shippingAccount: undefined,
              shippingRate: undefined,
            }),
          );
      }
    },
    [
      calculatedShippingRates,
      dispatch,
      shippingAccountFormValues,
      shippingRate?.token,
      shippingRateFormValue,
    ],
  );

  const shippingTypeSummaryLabel = useMemo(() => {
    if (!shippingType) return 'None';
    if (shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT) {
      return paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
        canShowChargeMeForShippingOption
        ? SUPPLIER_ACCOUNT_CHOICES.CHARGE_NOW.label
        : SHIPPING_TYPE_LABEL[SHIPPING_TYPE.SUPPLIER_ACCOUNT];
    }
    return SHIPPING_TYPE_LABEL[shippingType];
  }, [canShowChargeMeForShippingOption, paymentMethod, shippingType]);

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton>
            <Flex gap={1}>
              <Box flexShrink={0}>Shipping Options</Box>
              {!isExpanded ? (
                <Text
                  color="feature.secondary"
                  noOfLines={1}
                  whiteSpace="nowrap"
                >
                  {shippingTypeSummaryLabel}
                </Text>
              ) : null}
            </Flex>
          </AccordionButton>
          <AccordionPanel>
            <FormControl>
              <FormLabel>Shipping Fees</FormLabel>
              <RadioGroup
                value={selectedShippingFeesValue}
                onChange={handleChangeShippingType}
              >
                <Radio value="none">None</Radio>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.SUPPLIER_ACCOUNT]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SUPPLIER_ACCOUNT_CHOICES.BILL_LATER.value}
                    isDisabled={
                      !!optionDisabledMessages[SHIPPING_TYPE.SUPPLIER_ACCOUNT]
                    }
                  >
                    {SUPPLIER_ACCOUNT_CHOICES.BILL_LATER.label}
                  </Radio>
                </Tooltip>
                {canShowChargeMeForShippingOption ? (
                  <Radio value={SUPPLIER_ACCOUNT_CHOICES.CHARGE_NOW.value}>
                    {SUPPLIER_ACCOUNT_CHOICES.CHARGE_NOW.label}
                  </Radio>
                ) : null}
                <Collapse in={shippingType === SHIPPING_TYPE.SUPPLIER_ACCOUNT}>
                  <VStack pl={3} width="100%">
                    {paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
                    isCalculatedShippingEnabled ? (
                      <FormControl>
                        <FormLabel>Delivery Options</FormLabel>
                        {isFetchingCalculatedShipping ? (
                          <Center py={2}>
                            <Spinner size="md" />
                          </Center>
                        ) : calculatedShippingRates &&
                          calculatedShippingRates.length > 0 ? (
                          <RadioGroup
                            value={
                              shippingRate?.token ??
                              calculatedShippingRates[0].token
                            }
                            onChange={(token) => {
                              const nextRate = calculatedShippingRates.find(
                                (rate) => rate.token === token,
                              );
                              if (nextRate) {
                                updateShippingRate(nextRate);
                              }
                            }}
                          >
                            {calculatedShippingRates.map((rate) => (
                              <Radio key={rate.token} value={rate.token}>
                                <HStack>
                                  <Text fontWeight="bold">
                                    {toMoment(rate.arrivesOn)
                                      .utc()
                                      .format('dddd, MMM DD YYYY')}
                                  </Text>
                                  <Text>
                                    - Estimated delivery: by{' '}
                                    {toMoment(rate.arrivesOn)
                                      .utc()
                                      .format('h:mm A')}{' '}
                                  </Text>
                                </HStack>
                                <Text>
                                  {formatPrice(rate.price)} -{' '}
                                  {SHIPPING_METHOD_LABEL[rate.method]}
                                </Text>
                              </Radio>
                            ))}
                          </RadioGroup>
                        ) : (
                          <Text color="feature.secondary">
                            Unable to calculate shipping rates.
                          </Text>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl>
                        <FormLabel>Shipping Method</FormLabel>
                        <RadioGroup
                          value={shippingRateFormValue.method}
                          onChange={(value) => {
                            const shippingRate = LEGACY_SHIPPING_RATES.find(
                              (rate) => rate.method === value,
                            );
                            updateShippingRate(shippingRate);
                          }}
                        >
                          {LEGACY_SHIPPING_RATES.map((rate) => (
                            <Radio key={rate.method} value={rate.method}>
                              {SHIPPING_METHOD_LABEL[rate.method]}
                            </Radio>
                          ))}
                        </RadioGroup>
                      </FormControl>
                    )}
                  </VStack>
                </Collapse>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.CUSTOMER_ACCOUNT]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SHIPPING_TYPE.CUSTOMER_ACCOUNT}
                    isDisabled={
                      !!optionDisabledMessages[SHIPPING_TYPE.CUSTOMER_ACCOUNT]
                    }
                  >
                    {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.CUSTOMER_ACCOUNT]}
                  </Radio>
                </Tooltip>
                <Collapse in={shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT}>
                  <VStack p={0.5} pl={3} width="100%">
                    <FormControl isRequired>
                      <FormLabel>Shipping Account #</FormLabel>
                      <Input
                        key={shippingAccountFormValues.customersAccountNumber}
                        defaultValue={
                          shippingAccountFormValues.customersAccountNumber
                        }
                        onBlur={(event) =>
                          updateShippingAccount({
                            customersAccountNumber: event.target.value,
                          })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Carrier</FormLabel>
                      <Select
                        isClearable={false}
                        value={getCarrierOption(
                          shippingAccountFormValues.customersCarrier,
                        )}
                        options={Object.values(SHIPPING_CARRIER).map(
                          getCarrierOption,
                        )}
                        onChange={(option: { value: ShippingCarrier }) =>
                          updateShippingAccount({
                            customersCarrier: option.value,
                          })
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Method</FormLabel>
                      <Select
                        isClearable={false}
                        value={getMethodOption(
                          shippingAccountFormValues.shippingMethod,
                        )}
                        options={Object.values(shippingMethods).map(
                          getMethodOption,
                        )}
                        onChange={(option: { value: ShippingMethod }) =>
                          updateShippingAccount({
                            shippingMethod: option.value,
                          })
                        }
                      />
                    </FormControl>
                  </VStack>
                </Collapse>
                <Tooltip
                  label={optionDisabledMessages[SHIPPING_TYPE.PICKUP]}
                  placement="right"
                  shouldWrapChildren={true}
                >
                  <Radio
                    value={SHIPPING_TYPE.PICKUP}
                    isDisabled={!!optionDisabledMessages[SHIPPING_TYPE.PICKUP]}
                  >
                    {SHIPPING_TYPE_LABEL[SHIPPING_TYPE.PICKUP]}
                    {!optionDisabledMessages[SHIPPING_TYPE.PICKUP] ? (
                      <Tooltip label="This option is for customer pickup, local delivery, or other arrangements that preclude shipping fees.">
                        <OutlineCircleInfoIcon
                          color="feature.secondary"
                          boxSize={3}
                          verticalAlign="bottom"
                        />
                      </Tooltip>
                    ) : null}
                  </Radio>
                </Tooltip>
              </RadioGroup>
            </FormControl>
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
